import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { campaignsAPI, dashboardAPI, customFieldsAPI, advertisersAPI } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getPlatformColor } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { usePlatformAccess } from '../hooks/usePlatformAccess';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, Search, Loader2, AlertTriangle, Settings2, RefreshCw, Play, Pause, Power, UserPlus, Building2 } from 'lucide-react';

const CAMPAIGN_MODES = ["CPM", "CPC", "CPCV", "CPV", "CPL", "Cost per View", "Cost per Session"];

export const Campaigns = () => {
  const { canManageCampaigns, isSuperAdmin, user, isAdvertiser } = useAuth();
  
  // Platform access filtering
  const effectiveAdvertiserId = isAdvertiser() ? user?.advertiser_id : null;
  const { effectivePlatforms, isPlatformAllowed } = usePlatformAccess(effectiveAdvertiserId);
  
  const [campaigns, setCampaigns] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    client_name: '',
    selectedPlatforms: [],
    platform_budgets: [],
    total_budget: 0,
    start_date: '',
    end_date: '',
    campaign_mode: 'CPM',
    status: 'active',
    custom_fields: {},
    advertiser_id: '',
  });
  const [newCustomField, setNewCustomField] = useState({
    name: '',
    field_type: 'text',
    options: '',
    required: false,
  });
  const [saving, setSaving] = useState(false);
  const [controllingCampaign, setControllingCampaign] = useState(null);
  
  // Bulk selection state
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const [bulkControlling, setBulkControlling] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [selectedAdvertiserForAssign, setSelectedAdvertiserForAssign] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const PAGE_SIZES = [25, 50, 100, 200];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, platformsRes, customFieldsRes] = await Promise.all([
        campaignsAPI.getAll(),
        dashboardAPI.getPlatforms(),
        customFieldsAPI.getAll(),
      ]);
      setCampaigns(campaignsRes.data);
      setPlatforms(platformsRes.data.platforms);
      setCustomFields(customFieldsRes.data);
      
      // Only fetch advertisers if user has permissions (admin or super_admin)
      if (user?.role === 'super_admin' || user?.role === 'admin') {
        try {
          const advertisersRes = await advertisersAPI.getAll();
          setAdvertisers(advertisersRes.data || []);
        } catch (err) {
          // Silently fail - user just won't see advertiser assignment options
          setAdvertisers([]);
        }
      } else {
        setAdvertisers([]);
      }
    } catch (error) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate days between dates
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 1;
  };

  // Calculate expected daily run rate
  const calculateExpectedDailyRate = (deliverables, startDate, endDate) => {
    const days = calculateDays(startDate, endDate);
    return days > 0 ? (deliverables / days).toFixed(2) : 0;
  };

  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns.filter((campaign) => {
      const matchesSearch =
        campaign.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        campaign.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPlatform = filterPlatform === 'all' || 
        (campaign.platforms && campaign.platforms.includes(filterPlatform)) ||
        campaign.platform === filterPlatform;
      const matchesStatus = filterStatus === 'all' || campaign.status === filterStatus;
      return matchesSearch && matchesPlatform && matchesStatus;
    });
    
    // Sort by created_at descending (newest first), then by name
    filtered.sort((a, b) => {
      // First sort by created_at if available
      if (a.created_at && b.created_at) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      // Fall back to name sorting
      return (a.name || '').localeCompare(b.name || '');
    });
    
    return filtered;
  }, [campaigns, searchQuery, filterPlatform, filterStatus]);

  // Paginated campaigns
  const paginatedCampaigns = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredCampaigns.slice(startIndex, startIndex + pageSize);
  }, [filteredCampaigns, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredCampaigns.length / pageSize);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterPlatform, filterStatus, pageSize]);

  const openCreateDialog = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      client_name: '',
      selectedPlatforms: [],
      platform_budgets: [],
      total_budget: 0,
      start_date: '',
      end_date: '',
      campaign_mode: 'CPM',
      status: 'active',
      custom_fields: {},
      advertiser_id: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    const platformBudgets = campaign.platform_budgets || [];
    setFormData({
      name: campaign.name,
      client_name: campaign.client_name,
      selectedPlatforms: campaign.platforms || [campaign.platform],
      platform_budgets: platformBudgets,
      total_budget: campaign.total_budget || campaign.budget || 0,
      start_date: campaign.start_date,
      end_date: campaign.end_date,
      campaign_mode: campaign.campaign_mode || 'CPM',
      status: campaign.status,
      custom_fields: campaign.custom_fields || {},
      advertiser_id: campaign.advertiser_id || '',
    });
    setDialogOpen(true);
  };

  const handlePlatformToggle = (platform) => {
    setFormData((prev) => {
      const isSelected = prev.selectedPlatforms.includes(platform);
      let newPlatforms, newBudgets;
      
      if (isSelected) {
        newPlatforms = prev.selectedPlatforms.filter((p) => p !== platform);
        newBudgets = prev.platform_budgets.filter((pb) => pb.platform !== platform);
      } else {
        newPlatforms = [...prev.selectedPlatforms, platform];
        newBudgets = [...prev.platform_budgets, { 
          platform, 
          platform_campaign_id: '',
          budget: 0, 
          deliverables: 0, 
          daily_run_rate: 0,
          campaign_mode: prev.campaign_mode || 'CPM'
        }];
      }
      
      const totalBudget = newBudgets.reduce((sum, pb) => sum + (pb.budget || 0), 0);
      
      return {
        ...prev,
        selectedPlatforms: newPlatforms,
        platform_budgets: newBudgets,
        total_budget: totalBudget,
      };
    });
  };

  const updatePlatformBudget = (platform, field, value) => {
    setFormData((prev) => {
      const newBudgets = prev.platform_budgets.map((pb) => {
        if (pb.platform === platform) {
          // Handle string fields vs numeric fields
          const isStringField = field === 'campaign_mode' || field === 'platform_campaign_id';
          const parsedValue = isStringField ? value : (parseFloat(value) || 0);
          const updated = { ...pb, [field]: parsedValue };
          return updated;
        }
        return pb;
      });
      
      const totalBudget = newBudgets.reduce((sum, pb) => sum + (pb.budget || 0), 0);
      
      return {
        ...prev,
        platform_budgets: newBudgets,
        total_budget: totalBudget,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    
    setSaving(true);

    try {
      const data = {
        name: formData.name,
        platforms: formData.selectedPlatforms,
        platform_budgets: formData.platform_budgets,
        client_name: formData.client_name,
        total_budget: formData.total_budget,
        start_date: formData.start_date,
        end_date: formData.end_date,
        campaign_mode: formData.campaign_mode,
        status: formData.status,
        custom_fields: formData.custom_fields,
        advertiser_id: formData.advertiser_id || null,
      };

      if (editingCampaign) {
        await campaignsAPI.update(editingCampaign.id, data);
        toast.success('Campaign updated successfully');
      } else {
        await campaignsAPI.create(data);
        toast.success('Campaign created successfully');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Are you sure you want to delete "${campaign.name}"?`)) {
      return;
    }

    try {
      await campaignsAPI.delete(campaign.id);
      toast.success('Campaign deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  const handleSyncCampaign = async (campaign) => {
    // Check if any platform has a campaign ID configured
    const hasIds = campaign.platform_budgets?.some(pb => pb.platform_campaign_id);
    if (!hasIds) {
      toast.error('Please configure Platform Campaign IDs first');
      return;
    }

    try {
      const response = await campaignsAPI.syncData(campaign.id);
      const { synced_platforms, errors } = response.data;
      
      if (synced_platforms.length > 0) {
        toast.success(`Sync started for: ${synced_platforms.join(', ')}`);
      }
      if (errors.length > 0) {
        errors.forEach(err => toast.warning(err));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync campaign data');
    }
  };

  const handleBulkSync = async () => {
    // Check if any campaign has platform IDs configured
    const campaignsWithIds = campaigns.filter(c => 
      c.platform_budgets?.some(pb => pb.platform_campaign_id)
    );
    
    if (campaignsWithIds.length === 0) {
      toast.error('No campaigns have Platform Campaign IDs configured');
      return;
    }

    setSyncing(true);
    try {
      const response = await campaignsAPI.syncAll();
      const { total_synced, campaigns: results } = response.data;
      
      if (total_synced > 0) {
        toast.success(`Bulk sync started for ${total_synced} platform-campaign combinations`);
        
        // Show any errors
        results.forEach(r => {
          if (r.errors?.length > 0) {
            r.errors.forEach(err => toast.warning(`${r.campaign_name}: ${err}`));
          }
        });
      } else {
        toast.warning('No campaigns were synced. Check platform credentials and campaign IDs.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync campaigns');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateCustomField = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: newCustomField.name,
        field_type: newCustomField.field_type,
        options: newCustomField.field_type === 'dropdown' 
          ? newCustomField.options.split(',').map(o => o.trim()).filter(Boolean)
          : null,
        required: newCustomField.required,
      };
      await customFieldsAPI.create(data);
      toast.success('Custom field created');
      setNewCustomField({ name: '', field_type: 'text', options: '', required: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create custom field');
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    try {
      await customFieldsAPI.delete(fieldId);
      toast.success('Custom field deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete custom field');
    }
  };

  const handleCampaignControl = async (campaign, platform, action) => {
    const controlId = `${campaign.id}-${platform}-${action}`;
    setControllingCampaign(controlId);
    
    try {
      const response = await campaignsAPI.controlPlatform(campaign.id, platform, action);
      if (response.data.success) {
        toast.success(`Campaign ${action === 'start' ? 'started' : 'paused'} on ${platform}`);
        fetchData(); // Refresh to show updated status
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} campaign on ${platform}`);
    } finally {
      setControllingCampaign(null);
    }
  };

  const handleCampaignControlAll = async (campaign, action) => {
    const controlId = `${campaign.id}-all-${action}`;
    setControllingCampaign(controlId);
    
    try {
      const response = await campaignsAPI.controlAll(campaign.id, action);
      const { success_count, total_platforms, results } = response.data;
      
      if (success_count === total_platforms) {
        toast.success(`Campaign ${action === 'start' ? 'started' : 'paused'} on all ${total_platforms} platform(s)`);
      } else if (success_count > 0) {
        toast.warning(`Campaign ${action}ed on ${success_count}/${total_platforms} platforms`);
        // Show individual errors
        results.filter(r => !r.success).forEach(r => {
          toast.error(`${r.platform}: ${r.error}`);
        });
      } else {
        toast.error(`Failed to ${action} campaign on any platform`);
        results.forEach(r => {
          if (!r.success) toast.error(`${r.platform}: ${r.error}`);
        });
      }
      fetchData(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} campaign`);
    } finally {
      setControllingCampaign(null);
    }
  };

  // Get campaigns that can be controlled (have platform IDs)
  const controllableCampaigns = useMemo(() => {
    return filteredCampaigns.filter(c => 
      c.platform_budgets?.some(pb => pb.platform_campaign_id)
    );
  }, [filteredCampaigns]);

  // Get selected campaigns that can be controlled
  const selectedControllableCampaigns = useMemo(() => {
    return controllableCampaigns.filter(c => selectedCampaigns.has(c.id));
  }, [controllableCampaigns, selectedCampaigns]);

  // Handle individual checkbox toggle
  const handleSelectCampaign = (campaignId) => {
    setSelectedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    if (selectedCampaigns.size === controllableCampaigns.length) {
      setSelectedCampaigns(new Set());
    } else {
      setSelectedCampaigns(new Set(controllableCampaigns.map(c => c.id)));
    }
  };

  // Bulk control action
  const handleBulkControl = async (action) => {
    if (selectedControllableCampaigns.length === 0) {
      toast.error('No controllable campaigns selected');
      return;
    }

    setBulkControlling(true);
    let successCount = 0;
    let failCount = 0;

    for (const campaign of selectedControllableCampaigns) {
      try {
        const response = await campaignsAPI.controlAll(campaign.id, action);
        if (response.data.success_count > 0) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    setBulkControlling(false);
    
    if (successCount > 0) {
      toast.success(`${action === 'start' ? 'Started' : 'Paused'} ${successCount} campaign(s)`);
    }
    if (failCount > 0) {
      toast.warning(`Failed to ${action} ${failCount} campaign(s)`);
    }
    
    setSelectedCampaigns(new Set());
    fetchData();
  };

  // Bulk assign to advertiser
  const handleBulkAssign = async () => {
    if (selectedCampaigns.size === 0) {
      toast.error('No campaigns selected');
      return;
    }

    setBulkAssigning(true);
    try {
      const campaignIds = Array.from(selectedCampaigns);
      const advertiserId = selectedAdvertiserForAssign === '__unassign__' ? null : selectedAdvertiserForAssign;
      const response = await campaignsAPI.bulkAssign(campaignIds, advertiserId);
      toast.success(response.data.message);
      setAssignDialogOpen(false);
      setSelectedAdvertiserForAssign('');
      setSelectedCampaigns(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign campaigns');
    } finally {
      setBulkAssigning(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Campaigns</h1>
            <p className="text-sm text-slate-500 mt-1">Manage your advertising campaigns</p>
          </div>

          <div className="flex items-center gap-2">
            {canManageCampaigns() && (
              <Button 
                variant="outline" 
                onClick={handleBulkSync} 
                disabled={syncing || loading}
                data-testid="sync-all-campaigns-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync All'}
              </Button>
            )}
            {isSuperAdmin() && (
              <Button variant="outline" onClick={() => setCustomFieldDialogOpen(true)} data-testid="custom-fields-btn">
                <Settings2 className="w-4 h-4 mr-2" />
                Custom Fields
              </Button>
            )}
            {canManageCampaigns() && (
              <Button onClick={openCreateDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="create-campaign-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-campaigns-input"
                />
              </div>
              <Select value={filterPlatform} onValueChange={setFilterPlatform}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-platform-select">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {platforms.filter(platform => isPlatformAllowed(platform)).map((platform) => (
                    <SelectItem key={platform} value={platform}>
                      {platform}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="filter-status-select">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Action Bar */}
        {canManageCampaigns() && selectedCampaigns.size > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedCampaigns.size} campaign{selectedCampaigns.size > 1 ? 's' : ''} selected
                  </span>
                  <span className="text-xs text-blue-700">
                    ({selectedControllableCampaigns.length} controllable)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {advertisers.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAssignDialogOpen(true)}
                      disabled={bulkAssigning}
                      className="bg-white hover:bg-indigo-50 text-indigo-700 border-indigo-300"
                      data-testid="bulk-assign-btn"
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Assign to Advertiser
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkControl('start')}
                    disabled={bulkControlling || selectedControllableCampaigns.length === 0}
                    className="bg-white hover:bg-green-50 text-green-700 border-green-300"
                    data-testid="bulk-start-btn"
                  >
                    {bulkControlling ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Start All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkControl('stop')}
                    disabled={bulkControlling || selectedControllableCampaigns.length === 0}
                    className="bg-white hover:bg-amber-50 text-amber-700 border-amber-300"
                    data-testid="bulk-pause-btn"
                  >
                    {bulkControlling ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Pause className="w-4 h-4 mr-2" />
                    )}
                    Pause All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCampaigns(new Set())}
                    className="text-slate-600"
                    data-testid="clear-selection-btn"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Campaigns Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {canManageCampaigns() && (
                      <th className="w-10 py-3 px-3">
                        <Checkbox
                          checked={controllableCampaigns.length > 0 && selectedCampaigns.size === controllableCampaigns.length}
                          onCheckedChange={handleSelectAll}
                          disabled={controllableCampaigns.length === 0}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                    )}
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Campaign</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Advertiser</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Platforms</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Mode</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Budget</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    {canManageCampaigns() && (
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        {Array.from({ length: canManageCampaigns() ? 9 : 7 }).map((_, j) => (
                          <td key={j} className="py-2 px-4">
                            <div className="h-4 bg-slate-200 animate-pulse rounded" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : paginatedCampaigns.length > 0 ? (
                    paginatedCampaigns.map((campaign) => {
                      const isControllable = campaign.platform_budgets?.some(pb => pb.platform_campaign_id);
                      return (
                      <tr 
                        key={campaign.id} 
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedCampaigns.has(campaign.id) ? 'bg-blue-50/50' : ''}`}
                      >
                        {canManageCampaigns() && (
                          <td className="py-2 px-3">
                            <Checkbox
                              checked={selectedCampaigns.has(campaign.id)}
                              onCheckedChange={() => handleSelectCampaign(campaign.id)}
                              disabled={!isControllable}
                              title={isControllable ? 'Select for bulk action' : 'No platform IDs configured'}
                              data-testid={`select-campaign-${campaign.id}`}
                            />
                          </td>
                        )}
                        <td className="py-2 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{campaign.name}</span>
                            {campaign.daily_run_rate_exceeded && (
                              <AlertTriangle className="w-4 h-4 text-amber-500" title="Daily run rate exceeded" />
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-4">
                          {campaign.advertiser_name ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700">
                              <Building2 className="w-3 h-3" />
                              {campaign.advertiser_name}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Unassigned</span>
                          )}
                        </td>
                        <td className="py-2 px-4">
                          <div className="flex flex-wrap gap-1">
                            {(campaign.platforms || [campaign.platform]).map((p) => {
                              const platformBudget = campaign.platform_budgets?.find(pb => pb.platform === p);
                              const hasId = platformBudget?.platform_campaign_id;
                              return (
                                <span
                                  key={p}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${hasId ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-700'}`}
                                  title={hasId ? `ID: ${platformBudget.platform_campaign_id}` : 'No Campaign ID configured'}
                                >
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getPlatformColor(p) }} />
                                  {p}
                                  {hasId && <span className="text-green-500">✓</span>}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="py-2 px-4">
                          <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded">
                            {campaign.campaign_mode || 'CPM'}
                          </span>
                        </td>
                        <td className="text-right py-2 px-4 font-mono text-slate-700">
                          {formatCurrency(campaign.total_budget || campaign.budget || 0, campaign.advertiser_currency || 'USD')}
                        </td>
                        <td className="py-2 px-4 text-sm text-slate-500">
                          {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                        </td>
                        <td className="py-2 px-4">
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(campaign.status)}`}>
                            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                          </span>
                        </td>
                        {canManageCampaigns() && (
                          <td className="text-right py-2 px-4">
                            <div className="flex items-center justify-end gap-1">
                              {/* Campaign Control Buttons */}
                              {campaign.platform_budgets?.some(pb => pb.platform_campaign_id) && (
                                <>
                                  {campaign.status === 'active' ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleCampaignControlAll(campaign, 'stop')}
                                      disabled={controllingCampaign?.startsWith(campaign.id)}
                                      className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                      title="Pause campaign on all platforms"
                                      data-testid={`pause-campaign-${campaign.id}`}
                                    >
                                      {controllingCampaign === `${campaign.id}-all-stop` ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Pause className="w-4 h-4" />
                                      )}
                                    </Button>
                                  ) : campaign.status === 'paused' ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleCampaignControlAll(campaign, 'start')}
                                      disabled={controllingCampaign?.startsWith(campaign.id)}
                                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                      title="Start campaign on all platforms"
                                      data-testid={`start-campaign-${campaign.id}`}
                                    >
                                      {controllingCampaign === `${campaign.id}-all-start` ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Play className="w-4 h-4" />
                                      )}
                                    </Button>
                                  ) : null}
                                </>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`campaign-actions-${campaign.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {/* Per-Platform Control */}
                                  {campaign.platform_budgets?.filter(pb => pb.platform_campaign_id).map((pb) => (
                                    <DropdownMenuItem
                                      key={`control-${pb.platform}`}
                                      onClick={() => handleCampaignControl(
                                        campaign, 
                                        pb.platform, 
                                        campaign.status === 'active' ? 'stop' : 'start'
                                      )}
                                      disabled={controllingCampaign === `${campaign.id}-${pb.platform}-${campaign.status === 'active' ? 'stop' : 'start'}`}
                                    >
                                      {campaign.status === 'active' ? (
                                        <>
                                          <Pause className="w-4 h-4 mr-2 text-amber-500" />
                                          Pause on {pb.platform}
                                        </>
                                      ) : (
                                        <>
                                          <Play className="w-4 h-4 mr-2 text-green-500" />
                                          Start on {pb.platform}
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  ))}
                                  {campaign.platform_budgets?.some(pb => pb.platform_campaign_id) && (
                                    <div className="border-t my-1" />
                                  )}
                                  <DropdownMenuItem onClick={() => handleSyncCampaign(campaign)}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Sync Data
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openEditDialog(campaign)}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(campaign)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        )}
                      </tr>
                    );})
                  ) : (
                    <tr>
                      <td colSpan={canManageCampaigns() ? 9 : 7} className="py-12 text-center text-slate-400">
                        No campaigns found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {filteredCampaigns.length > 0 && (
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredCampaigns.length)} of {filteredCampaigns.length} campaigns</span>
                  <span className="text-slate-300">|</span>
                  <span>Rows per page:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(val) => setPageSize(parseInt(val))}
                  >
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map(size => (
                        <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-1 text-sm">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage >= totalPages}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Campaign Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
              </DialogTitle>
              <DialogDescription>
                Configure campaign details, platforms, and deliverables
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Campaign Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="campaign-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_name">Client Name</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      data-testid="campaign-client-input"
                    />
                  </div>
                </div>

                {/* Advertiser Assignment */}
                {advertisers.length > 0 && (
                  <div className="space-y-2">
                    <Label>Assign to Advertiser</Label>
                    <Select
                      value={formData.advertiser_id || '__none__'}
                      onValueChange={(value) => setFormData({ ...formData, advertiser_id: value === '__none__' ? '' : value })}
                    >
                      <SelectTrigger data-testid="campaign-advertiser-select">
                        <SelectValue placeholder="Select an advertiser (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No advertiser (Unassigned)</SelectItem>
                        {advertisers.map((adv) => (
                          <SelectItem key={adv.id} value={adv.id}>
                            {adv.name} {adv.currency && `(${adv.currency})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Assigning to an advertiser links this campaign's spend data for unified reporting
                    </p>
                  </div>
                )}

                {/* Campaign Mode & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Campaign Mode</Label>
                    <Select
                      value={formData.campaign_mode}
                      onValueChange={(value) => setFormData({ ...formData, campaign_mode: value })}
                    >
                      <SelectTrigger data-testid="campaign-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CAMPAIGN_MODES.map((mode) => (
                          <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="campaign-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      data-testid="campaign-start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                      data-testid="campaign-end-date-input"
                    />
                  </div>
                </div>

                {/* Platform Selection */}
                <div className="space-y-2">
                  <Label>Select Platforms</Label>
                  <div className="grid grid-cols-3 gap-2 p-3 border rounded-md bg-slate-50">
                    {platforms.filter(platform => isPlatformAllowed(platform)).map((platform) => (
                      <label key={platform} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={formData.selectedPlatforms.includes(platform)}
                          onCheckedChange={() => handlePlatformToggle(platform)}
                        />
                        <span className="text-sm">{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Platform Budgets & Deliverables */}
                {formData.selectedPlatforms.length > 0 && (
                  <div className="space-y-3">
                    <Label>Platform Details</Label>
                    <div className="border rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-2 font-medium text-slate-600">Platform</th>
                            <th className="text-left p-2 font-medium text-slate-600">
                              Campaign ID <span className="text-red-500">*</span>
                            </th>
                            <th className="text-left p-2 font-medium text-slate-600">Budget ($)</th>
                            <th className="text-left p-2 font-medium text-slate-600">Deliverables</th>
                            <th className="text-left p-2 font-medium text-slate-600">Daily Rate</th>
                            <th className="text-left p-2 font-medium text-slate-600">Mode</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.platform_budgets.map((pb) => {
                            const expectedRate = calculateExpectedDailyRate(pb.deliverables, formData.start_date, formData.end_date);
                            const isExceeded = pb.daily_run_rate > expectedRate * 1.1 && pb.deliverables > 0;
                            
                            return (
                              <tr key={pb.platform} className="border-t">
                                <td className="p-2">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getPlatformColor(pb.platform) }} />
                                    {pb.platform}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="text"
                                    value={pb.platform_campaign_id || ''}
                                    onChange={(e) => updatePlatformBudget(pb.platform, 'platform_campaign_id', e.target.value)}
                                    className="h-8 w-28"
                                    placeholder="e.g. 12345"
                                    required
                                    data-testid={`platform-campaign-id-${pb.platform}`}
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={pb.budget || ''}
                                    onChange={(e) => updatePlatformBudget(pb.platform, 'budget', e.target.value)}
                                    className="h-8 w-20"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    value={pb.deliverables || ''}
                                    onChange={(e) => updatePlatformBudget(pb.platform, 'deliverables', e.target.value)}
                                    className="h-8 w-20"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="p-2">
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={pb.daily_run_rate || ''}
                                      onChange={(e) => updatePlatformBudget(pb.platform, 'daily_run_rate', e.target.value)}
                                      className={`h-8 w-16 ${isExceeded ? 'border-amber-500 bg-amber-50' : ''}`}
                                      placeholder="0"
                                    />
                                    {isExceeded && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <Select
                                    value={pb.campaign_mode || formData.campaign_mode || 'CPM'}
                                    onValueChange={(value) => updatePlatformBudget(pb.platform, 'campaign_mode', value)}
                                  >
                                    <SelectTrigger className="h-8 w-28">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {CAMPAIGN_MODES.map((mode) => (
                                        <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-slate-50">
                          <tr className="border-t">
                            <td className="p-2 font-medium">Total</td>
                            <td className="p-2"></td>
                            <td className="p-2 font-mono font-medium">${formData.total_budget.toFixed(2)}</td>
                            <td colSpan={3}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <p className="text-xs text-slate-500">
                      Enter the Campaign ID from each platform to fetch reports. This is the ID shown in your ad platform's dashboard.
                    </p>
                    {formData.platform_budgets.some((pb) => {
                      const expectedRate = calculateExpectedDailyRate(pb.deliverables, formData.start_date, formData.end_date);
                      return pb.daily_run_rate > expectedRate * 1.1 && pb.deliverables > 0;
                    }) && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Daily run rate exceeds expected rate. An email notification will be sent.
                      </p>
                    )}
                  </div>
                )}

                {/* Custom Fields */}
                {customFields.length > 0 && (
                  <div className="space-y-3">
                    <Label>Custom Fields</Label>
                    <div className="space-y-3 p-3 border rounded-md bg-slate-50">
                      {customFields.map((field) => (
                        <div key={field.id} className="space-y-1">
                          <Label className="text-sm">{field.label || field.name} {field.required && <span className="text-red-500">*</span>}</Label>
                          {field.field_type === 'dropdown' ? (
                            <Select
                              value={formData.custom_fields[field.name] || ''}
                              onValueChange={(value) => setFormData({
                                ...formData,
                                custom_fields: { ...formData.custom_fields, [field.name]: value }
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Select ${field.label || field.name}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {(field.options || []).map((opt, idx) => {
                                  // Handle both {label, value} objects and plain strings
                                  const optValue = typeof opt === 'object' ? opt.value : opt;
                                  const optLabel = typeof opt === 'object' ? opt.label : opt;
                                  return (
                                    <SelectItem key={optValue || idx} value={optValue}>{optLabel}</SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type={field.field_type === 'number' ? 'number' : 'text'}
                              value={formData.custom_fields[field.name] || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                custom_fields: { ...formData.custom_fields, [field.name]: e.target.value }
                              })}
                              required={field.required}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="campaign-save-btn">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingCampaign ? (
                    'Update Campaign'
                  ) : (
                    'Create Campaign'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Custom Fields Management Dialog */}
        <Dialog open={customFieldDialogOpen} onOpenChange={setCustomFieldDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-heading">Manage Custom Fields</DialogTitle>
              <DialogDescription>
                Create custom fields for campaigns (text, number, or dropdown)
              </DialogDescription>
            </DialogHeader>
            
            {/* Existing Fields */}
            {customFields.length > 0 && (
              <div className="space-y-2">
                <Label>Existing Fields</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {customFields.map((field) => (
                    <div key={field.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{field.name}</span>
                        <span className="text-xs text-slate-500 ml-2">({field.field_type})</span>
                        {field.required && <span className="text-xs text-red-500 ml-1">Required</span>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCustomField(field.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Field */}
            <form onSubmit={handleCreateCustomField} className="space-y-4 pt-4 border-t">
              <Label>Add New Field</Label>
              <div className="space-y-3">
                <Input
                  placeholder="Field Name"
                  value={newCustomField.name}
                  onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                  required
                />
                <Select
                  value={newCustomField.field_type}
                  onValueChange={(value) => setNewCustomField({ ...newCustomField, field_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                  </SelectContent>
                </Select>
                {newCustomField.field_type === 'dropdown' && (
                  <Input
                    placeholder="Options (comma separated)"
                    value={newCustomField.options}
                    onChange={(e) => setNewCustomField({ ...newCustomField, options: e.target.value })}
                    required
                  />
                )}
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={newCustomField.required}
                    onCheckedChange={(checked) => setNewCustomField({ ...newCustomField, required: checked })}
                  />
                  Required field
                </label>
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Add Field
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Assign Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-500" />
                Assign Campaigns to Advertiser
              </DialogTitle>
              <DialogDescription>
                Assign {selectedCampaigns.size} selected campaign{selectedCampaigns.size > 1 ? 's' : ''} to an advertiser for unified reporting
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Advertiser</Label>
                <Select
                  value={selectedAdvertiserForAssign}
                  onValueChange={setSelectedAdvertiserForAssign}
                >
                  <SelectTrigger data-testid="bulk-assign-advertiser-select">
                    <SelectValue placeholder="Choose an advertiser..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassign__">Remove assignment (Unassign)</SelectItem>
                    {advertisers.map((adv) => (
                      <SelectItem key={adv.id} value={adv.id}>
                        {adv.name} {adv.currency && `(${adv.currency})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedAdvertiserForAssign && selectedAdvertiserForAssign !== '__unassign__' && (
                <div className="p-3 bg-indigo-50 rounded-md text-sm text-indigo-700">
                  All selected campaigns will be linked to this advertiser's spend data and currency ({advertisers.find(a => a.id === selectedAdvertiserForAssign)?.currency || 'USD'})
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleBulkAssign}
                disabled={bulkAssigning || !selectedAdvertiserForAssign}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-bulk-assign-btn"
              >
                {bulkAssigning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <Building2 className="w-4 h-4 mr-2" />
                    {selectedAdvertiserForAssign === '__unassign__' ? 'Unassign' : 'Assign'} Campaigns
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
